import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import {
  ColorStyleButton,
  FormattingToolbar,
  FormattingToolbarController,
  getDefaultReactSlashMenuItems,
  getFormattingToolbarItems,
  SuggestionMenuController,
  useBlockNoteEditor,
  useComponentsContext,
  useDictionary,
  useCreateBlockNote,
  useEditorState,
} from '@blocknote/react';
import { filterSuggestionItems } from '@blocknote/core/extensions';
import { en } from '@blocknote/core/locales';
import {
  getMultiColumnSlashMenuItems,
  locales as multiColumnLocales,
  withMultiColumn,
} from '@blocknote/xl-multi-column';
import { api } from '../lib/api';
import type {
  Block,
  BlockSchema,
  DefaultStyleSchema,
  InlineContent,
  InlineContentSchema,
  Styles,
  TableContent,
} from '@blocknote/core';
import { BlockNoteSchema, COLORS_DEFAULT, defaultBlockSpecs } from '@blocknote/core';
import {
  createReactBlockSpec,
  ImageBlock,
  ImageToExternalHTML,
  ReactImageBlock,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { useEffect, useRef, useState } from 'react';
import './main-content-editor.css';

/**
 * TEMPORARY float support (operator request 2026-09-10, deliberately no task):
 * the built-in image block gains a `float` prop ('none' | 'left' | 'right')
 * so campaign content can wrap text around an image on the landing pages.
 *
 * The editor schema is the default one with the image block replaced by a
 * `createReactBlockSpec` extension of BlockNote's own React image spec
 * (ReactImageBlock) — only `parse` is overridden, to read the stored
 * `data-float` attribute back; `toExternalHTML` stays the built-in one and
 * the attribute layer of the extended propSchema writes `data-float` onto the
 * exported <img> by itself (non-default values only, so stored fragments of
 * untouched images are byte-identical).
 *
 * Known limitation: the editor's blocks are separate flex containers, so the
 * text wrap itself is only visible on the landing page (globals.css keys on
 * img[data-float]); in-editor the selection state of the toolbar buttons is
 * the only feedback.
 */
type ImageFloat = 'none' | 'left' | 'right';

const baseImageSpec = ReactImageBlock();
const baseImageImplementation = baseImageSpec.implementation;

/**
 * The propSchema as a standalone const: an inline literal with the extra
 * `float` key trips TypeScript's excess-property check against the exact
 * built-in propSchema type of the spread config.
 */
const floatPropSchema = {
  ...baseImageSpec.config.propSchema,
  float: { default: 'none', values: ['none', 'left', 'right'] as const },
};

const floatImageSpec = createReactBlockSpec(
  {
    ...baseImageSpec.config,
    propSchema: floatPropSchema,
  },
  {
    meta: baseImageImplementation.meta,
    runsBefore: baseImageImplementation.runsBefore,
    // render/toExternalHTML forward to BlockNote's exported React image
    // components (the spec-level implementation fields are typed with core
    // signatures, so the base's own functions do not type-fit here).
    render: (props) => <ImageBlock {...props} />,
    toExternalHTML: (props) => <ImageToExternalHTML {...props} />,
    parse: (element) => {
      const base = baseImageImplementation.parse?.(element);
      if (base === undefined) {
        return undefined;
      }
      const float = element.getAttribute('data-float');
      return float === 'left' || float === 'right' ? { ...base, float } : base;
    },
  }
)();

/**
 * Multi-column (experiment branch blocknote-upgrades): the columnList/column
 * block specs come from the paid @blocknote/xl-multi-column package (license
 * "GPL-3.0 OR PROPRIETARY" — fine for this evaluation branch, but production
 * use needs a BlockNote Pro subscription or GPL-3.0 compliance for the whole
 * distributed app). The specs are plain tiptap nodes rendering DOM
 * (div.bn-block-column-list with inline flex styles), so the editor works
 * under the mantine view as-is. The exported fragment is made self-contained
 * (column widths that survive pages without BlockNote's CSS) by
 * `selfContainedColumnFlex` below; round-trip parse keys on the
 * data-node-type attributes.
 */
const mainContentSchema = withMultiColumn(
  BlockNoteSchema.create({
    blockSpecs: { ...defaultBlockSpecs, image: floatImageSpec },
  })
);

/**
 * Multi-column dictionary (HF-155 stand follow-up): the xl package's UI reads
 * its strings from `editor.dictionary.multi_column` and throws
 * "Multi-column dictionary not found" as soon as that key is absent — e.g. on
 * opening the "/" menu, whose item list is an async callback (hence "Uncaught
 * (in promise)"). Without an explicit option the editor falls back to core's
 * built-in `en`, which knows nothing about columns, so the base English
 * dictionary is spread with the xl package's own English chunk attached under
 * its key. `dictionary?: Dictionary & Record<string, any>` is precisely this
 * extension seam.
 */
const mainContentDictionary = { ...en, multi_column: multiColumnLocales.en };

/**
 * Self-contained column widths (HF-155 stand follow-up): the xl package's
 * export writes only `flex-grow: <width>;` on each column div, but inside the
 * editor the ratio actually comes from its stylesheet's
 * `.bn-block-column { flex: 1 }` — flex-basis 0. A page without BlockNote's
 * CSS (every landing) falls back to basis auto, so columns size by content
 * and a resize made in the editor is ignored there (stand report
 * 2026-10-19). Rewriting the inline style to the full `flex: <w> 1 0%`
 * shorthand carries the basis inside the fragment; the computed style equals
 * the editor's, and the round-trip parse still keys on `data-width`, not the
 * style.
 */
function selfContainedColumnFlex(html: string): string {
  return html.replace(
    /(data-node-type="column"[^>]*style=")flex-grow: ([\d.]+);(")/g,
    '$1flex: $2 1 0%;$3'
  );
}

const U_FFFC = '\uFFFC';

/**
 * U+FFFC export strip (task 110-05, QA r30 BUG-01 / #169 tail): BlockNote's
 * lossy exporter appends a U+FFFC (Object Replacement Character) text node to
 * every inline-content block whose content DOM is empty, and the #169 deletion
 * bugs leave such paragraphs behind next to images — inside a column they
 * export as `<p data-nesting-level="2">￼</p>`, which the former exact-string
 * match (`split('<p>￼</p>')`) let through to the landing, where it rendered as
 * the dashed "[Obj]" box. This cleanup replaces that match, on the saved-HTML
 * seam only (the onChange emission):
 *
 * 1. the legacy exact form `<p>￼</p>` — a deliberate empty paragraph — still
 *    becomes `<br/>` (the pre-task behaviour, unchanged);
 * 2. any other paragraph whose content is only U+FFFC — under any `<p>`
 *    attributes (data-nesting-level, classes), with optional whitespace around
 *    the character, wrapped in (nested) spans with any attributes — is removed
 *    entirely: it is deletion debris, not an authored empty line (the QA
 *    workaround was deleting the line by hand), and its landing rendering was
 *    a broken "[Obj]" box;
 * 3. a U+FFFC sitting next to real text inside a contentful paragraph is
 *    deleted character-wise; the text stays.
 *
 * Nothing contentful is touched: a paragraph keeps its text, an `<img>` is
 * never U+FFFC and never matches. Already-saved fragments are not migrated
 * (HF-155 follow-up-2 precedent) — a campaign picks the clean export up on its
 * next edit-and-save. The paste path's `blocksToHTMLLossy` (pasteHandler)
 * stays unstripped: its output is pasted INTO the document, not saved, and
 * every save re-serializes the document through this emission, so nothing
 * reaches the stored HTML without passing here.
 */

/** A `<p>` element with any (or no) attributes and its body, up to `</p>`. */
const PARAGRAPH_HTML = /<p(?:\s[^>]*)?>((?:(?!<\/p>)[\s\S])*)<\/p>/g;

/** An innermost `<span>` wrapper: any attributes, no nested span inside. */
const INNERMOST_SPAN_HTML = /<span[^>]*>((?:(?!<\/?span)[\s\S])*)<\/span>/g;

/**
 * True when the paragraph body holds nothing but U+FFFC characters and
 * whitespace, possibly wrapped in (nested) spans with arbitrary attributes —
 * the shapes the exporter writes for an emptied paragraph. Requires at least
 * one U+FFFC: a plain empty `<p></p>` carries no export artifact and is not
 * ours to touch.
 */
function isPureObjectReplacementBody(body: string): boolean {
  if (!body.includes(U_FFFC)) {
    return false;
  }
  // Unwrap innermost spans repeatedly: colour marks render as nested span
  // layers around the character (`<span style=…><span>￼</span></span>`). The
  // guard keeps a span carrying real content from unwrapping into a false
  // positive — its text survives the reduction and fails the final test.
  let rest = body;
  for (;;) {
    const unwrapped = rest.replace(INNERMOST_SPAN_HTML, (_match, inner: string) => inner);
    if (unwrapped === rest) {
      break;
    }
    rest = unwrapped;
  }
  return /^[\s\uFFFC]*$/.test(rest);
}

export function stripObjectReplacementParagraphs(html: string): string {
  // The paragraph pass first, while the U+FFFC still marks the debris: a body
  // that is only the character (± whitespace, under any `<p>` attributes) is
  // an authored empty line — it keeps the `<br/>` convention (the former
  // match's semantics, AC 2; `\s` does not cover U+FFFC, hence the explicit
  // class). A body whose residue after the span unwrap still carries markup
  // (a span wrapper) is the exporter's styled-empty artifact — the deletion
  // debris the QA workaround removed by hand — and the paragraph leaves
  // nothing behind. A contentful paragraph never passes isPure… above and
  // survives this pass intact; its stray character is removed by the second
  // pass, below.
  let result = html.replace(PARAGRAPH_HTML, (match, body: string) => {
    if (!isPureObjectReplacementBody(body)) {
      return match;
    }
    return /^[\s\uFFFC]*$/.test(body) ? '<br/>' : '';
  });
  // The character-level pass: a U+FFFC next to real text is removed where it
  // stands; the text stays (the task's boundary case).
  result = result.split(U_FFFC).join('');
  return result;
}

interface MainContentEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  /**
   * The editable surface's accessible name (042-02's a11y contract). A
   * contenteditable is not labelable, so the name cannot come from a wrapping
   * label — it is stamped onto the .bn-editor div directly. Defaults to
   * "Main Content"; the epic-106 Intro editor passes "Intro" so the two
   * mounted instances stay distinguishable.
   */
  ariaLabel?: string;
}

/**
 * Extended color palette: BlockNote 0.54's built-in color picker hardcodes
 * its palette to the 10 theme colors with no option to extend it, while the
 * style system itself accepts any string style value and the exporter writes
 * unknown values verbatim. So the toolbar's color dropdown is replaced with
 * one that appends the two monochrome entries the landing pages need. The
 * new entries are stored as the style values "black"/"white"; their in-editor
 * display comes from the CSS rules in main-content-editor.css (BlockNote only
 * ships display rules for its own palette names).
 */
const EXTRA_COLORS = [
  { name: 'black', label: 'Black' },
  { name: 'white', label: 'White' },
  { name: 'darkestblue', label: 'Black Blue' },
] as const;

/** Mirrors the palette list of BlockNote's built-in ColorPicker. */
const BUILT_IN_COLOR_NAMES = [
  'default',
  'gray',
  'brown',
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
] as const;

/**
 * Colour whitelist (task 050-07, QA #69): on every path HTML enters the
 * editor — the hydration of a stored fragment and a paste — colour values are
 * normalized to palette names (BlockNote's ten plus the extras above). A
 * value that is the exported CSS of a palette entry maps back to its name;
 * any other value (a pasted `rgb(140, 140, 140)`, a stored `rgb(155, 154,
 * 151)` from a once-normalized `gray`) is dropped. BlockNote's inline-style
 * parse rule captures ANY `style="color: …"` verbatim and the DOM reports it
 * as `rgb(r, g, b)`, so without this the picker (which compares style values
 * against palette names) shows *Auto* for a colour the page still renders,
 * and it cannot be removed except by re-setting *Auto*.
 *
 * The export path is untouched: a document that only holds palette names
 * serializes exactly as before (the exporters write `COLORS_DEFAULT[name]`
 * for names and the value verbatim otherwise — there is no other value left
 * to write).
 */

/** The CSS each extra palette entry renders as (editor + landing CSS). */
const EXTRA_COLOR_CSS = {
  black: ['#000000'],
  white: ['#ffffff'],
  darkestblue: ['#003366', '#036'],
} as const;

/** `default` + BlockNote's nine theme names + the three extras. */
const PALETTE_COLOUR_NAMES: ReadonlySet<string> = new Set([
  'default',
  ...Object.keys(COLORS_DEFAULT),
  ...Object.keys(EXTRA_COLOR_CSS),
]);

type ColourKind = 'textColor' | 'backgroundColor';

/**
 * Detached scratch element for CSS normalization: assigning a colour to
 * `style.color` / `style.backgroundColor` canonicalizes the spelling the same
 * way the DOM does when it parses HTML (`#9b9a97` and `rgb(155, 154, 151)`
 * both become `rgb(155, 154, 151)`, `#036` and `#003366` both `rgb(0, 51,
 * 102)`; an unparseable value such as the non-CSS `darkestblue` leaves the
 * property empty), so hex and rgb spellings of the same colour compare equal.
 */
let scratchStyleElement: HTMLSpanElement | undefined;

/** Reverse maps (canonical CSS → palette name), built lazily per kind. */
const colourNameByCanonicalCss: Record<ColourKind, Map<string, string> | undefined> = {
  textColor: undefined,
  backgroundColor: undefined,
};

function canonicalCssColour(kind: ColourKind, value: string): string {
  scratchStyleElement ??= document.createElement('span');
  if (kind === 'textColor') {
    scratchStyleElement.style.removeProperty('color');
    scratchStyleElement.style.color = value;
    return scratchStyleElement.style.color;
  }
  scratchStyleElement.style.removeProperty('background-color');
  scratchStyleElement.style.backgroundColor = value;
  return scratchStyleElement.style.backgroundColor;
}

function paletteNamesByCss(kind: ColourKind): Map<string, string> {
  const cached = colourNameByCanonicalCss[kind];
  if (cached !== undefined) {
    return cached;
  }
  const map = new Map<string, string>();
  for (const [name, { text, background }] of Object.entries(COLORS_DEFAULT)) {
    map.set(canonicalCssColour(kind, kind === 'textColor' ? text : background), name);
  }
  for (const [name, spellings] of Object.entries(EXTRA_COLOR_CSS)) {
    for (const spelling of spellings) {
      map.set(canonicalCssColour(kind, spelling), name);
    }
  }
  colourNameByCanonicalCss[kind] = map;
  return map;
}

/**
 * Map one colour value onto the palette: a palette name stays; a value equal
 * to a palette entry's exported CSS (in any spelling the DOM canonicalizes)
 * maps back to its name; anything else → undefined (drop the style / reset
 * the prop to 'default').
 */
function normalizeColourValue(kind: ColourKind, value: string): string | undefined {
  if (PALETTE_COLOUR_NAMES.has(value)) {
    return value;
  }
  const canonical = canonicalCssColour(kind, value);
  if (canonical !== '') {
    const name = paletteNamesByCss(kind).get(canonical);
    if (name !== undefined) {
      return name;
    }
  }
  return undefined;
}

// ---- The block-tree walk ---------------------------------------------------
//
// Concrete schema types of this editor, for the walk below.

type MainContentBlock = Block<
  (typeof mainContentSchema)['blockSchema'],
  (typeof mainContentSchema)['inlineContentSchema'],
  (typeof mainContentSchema)['styleSchema']
>;
type MainContentInlineContent = InlineContent<
  (typeof mainContentSchema)['inlineContentSchema'],
  (typeof mainContentSchema)['styleSchema']
>;
type MainContentStyles = Styles<(typeof mainContentSchema)['styleSchema']>;
type MainContentTableContent = TableContent<
  (typeof mainContentSchema)['inlineContentSchema'],
  (typeof mainContentSchema)['styleSchema']
>;

function normalizeStyleColours(styles: MainContentStyles): void {
  for (const kind of ['textColor', 'backgroundColor'] as const) {
    const value = styles[kind];
    if (value === undefined) {
      continue;
    }
    const normalized = normalizeColourValue(kind, value);
    if (normalized === undefined) {
      // An un-mappable value leaves the document entirely: the mark is
      // removed, so the picker shows *Auto* and the export writes no span.
      delete styles[kind];
    } else {
      styles[kind] = normalized;
    }
  }
}

function normalizeInlineContentColours(items: MainContentInlineContent[]): void {
  for (const item of items) {
    if (item.type === 'text') {
      normalizeStyleColours(item.styles);
    } else if (item.type === 'link') {
      normalizeInlineContentColours(item.content);
    }
  }
}

function normalizeTableContentColours(table: MainContentTableContent): void {
  for (const row of table.rows) {
    for (const cell of row.cells) {
      if (Array.isArray(cell)) {
        normalizeInlineContentColours(cell);
      } else {
        // A tableCell is a block-like carrier: the same two colour props,
        // plus its own inline content.
        cell.props.textColor = normalizeColourValue('textColor', cell.props.textColor) ?? 'default';
        cell.props.backgroundColor =
          normalizeColourValue('backgroundColor', cell.props.backgroundColor) ?? 'default';
        normalizeInlineContentColours(cell.content);
      }
    }
  }
}

/**
 * Block-level colour props — e.g. an `<h2 style="color: #9b9a97">` the
 * exporter writes (it stores only the inline style, so a block colour saved
 * once came back as the normalized rgb spelling). Props types are readonly
 * maps, so the colours are normalized into a rebuilt props object; blocks
 * without a given colour prop (image/audio have no text colour, code blocks
 * have neither) pass that prop through.
 */
function normalizePropsColours(props: MainContentBlock['props']): MainContentBlock['props'] {
  if ('textColor' in props && 'backgroundColor' in props) {
    return {
      ...props,
      textColor: normalizeColourValue('textColor', props.textColor) ?? 'default',
      backgroundColor: normalizeColourValue('backgroundColor', props.backgroundColor) ?? 'default',
    };
  }
  if ('textColor' in props) {
    return { ...props, textColor: normalizeColourValue('textColor', props.textColor) ?? 'default' };
  }
  if ('backgroundColor' in props) {
    return {
      ...props,
      backgroundColor: normalizeColourValue('backgroundColor', props.backgroundColor) ?? 'default',
    };
  }
  return props;
}

function normalizeBlockColours(block: MainContentBlock): void {
  block.props = normalizePropsColours(block.props);
  const content = block.content;
  if (Array.isArray(content)) {
    // Inline content — also the shape of a code block's plain content, whose
    // items can never carry styles (the walk is a no-op there).
    normalizeInlineContentColours(content);
  } else if (content !== undefined) {
    normalizeTableContentColours(content);
  }
  for (const child of block.children) {
    normalizeBlockColours(child);
  }
}

/**
 * Normalize colour values in a freshly parsed block tree (task 050-07):
 * `props.textColor` / `props.backgroundColor` on every block (children and
 * table cells included) and `styles.textColor` / `styles.backgroundColor` on
 * every styled text become palette names — see the whitelist comment above.
 * Walks and rewrites the given tree in place (both callers hand it a fresh
 * `tryParseHTMLToBlocks` result) and returns it for inline use.
 */
export function normalizeColours(blocks: MainContentBlock[]): MainContentBlock[] {
  for (const block of blocks) {
    normalizeBlockColours(block);
  }
  return blocks;
}

/** Local copy of BlockNote's unexported ColorIcon swatch. */
function ColorIcon(props: {
  textColor: string | undefined;
  backgroundColor: string | undefined;
  size?: number;
}) {
  const size = props.size ?? 16;
  return (
    <div
      className="bn-color-icon"
      data-text-color={props.textColor || 'default'}
      data-background-color={props.backgroundColor || 'default'}
      style={{
        pointerEvents: 'none',
        fontSize: (size * 0.75).toString() + 'px',
        height: size.toString() + 'px',
        lineHeight: size.toString() + 'px',
        textAlign: 'center',
        width: size.toString() + 'px',
      }}
    >
      A
    </div>
  );
}

/**
 * ColorStyleButton + EXTRA_COLORS: a drop-in replacement for the built-in
 * ColorStyleButton, modeled on its upstream source with the picker palette
 * extended. Built-in entries keep their localized dictionary labels.
 */
function ExtendedColorStyleButton() {
  const Components = useComponentsContext()!;
  const dict = useDictionary();
  const editor = useBlockNoteEditor<BlockSchema, InlineContentSchema, DefaultStyleSchema>();

  const state = useEditorState({
    editor,
    selector: ({ editor }) => {
      // Hide the button when it cannot apply: read-only editor, or a
      // selection without inline content (same guard as upstream).
      if (
        !editor.isEditable ||
        !(editor.getSelection()?.blocks || [editor.getTextCursorPosition().block]).find(
          (block) => block.content !== undefined
        )
      ) {
        return undefined;
      }
      // getActiveStyles() types string styles as `string | true`; narrow to
      // the string form (boolean form never occurs for these propSchemas).
      const styles = editor.getActiveStyles();
      const textColor = typeof styles.textColor === 'string' ? styles.textColor : 'default';
      const backgroundColor =
        typeof styles.backgroundColor === 'string' ? styles.backgroundColor : 'default';
      return { textColor, backgroundColor };
    },
  });

  if (state === undefined) {
    return null;
  }

  function setColor(kind: 'text' | 'background', name: string) {
    if (name === 'default') {
      if (kind === 'text') {
        editor.removeStyles({ textColor: name });
      } else {
        editor.removeStyles({ backgroundColor: name });
      }
    } else if (kind === 'text') {
      editor.addStyles({ textColor: name });
    } else {
      editor.addStyles({ backgroundColor: name });
    }
    // timeout needed for compatibility with Mantine Toolbar useFocusTrap
    setTimeout(() => editor.focus());
  }

  function renderItems(kind: 'text' | 'background', current: string) {
    const items = [
      ...BUILT_IN_COLOR_NAMES.map((name) => ({
        name,
        label: dict.color_picker.colors[name],
      })),
      ...EXTRA_COLORS,
    ];
    return items.map(({ name, label }) => (
      <Components.Generic.Menu.Item
        key={kind + '-' + name}
        onClick={() => setColor(kind, name)}
        data-test={`${kind}-color-${name}`}
        checked={current === name}
        icon={
          <ColorIcon
            textColor={kind === 'text' ? name : undefined}
            backgroundColor={kind === 'background' ? name : undefined}
          />
        }
      >
        {label}
      </Components.Generic.Menu.Item>
    ));
  }

  return (
    <Components.Generic.Menu.Root>
      <Components.Generic.Menu.Trigger>
        <Components.FormattingToolbar.Button
          className="bn-button"
          data-test="colors"
          label={dict.formatting_toolbar.colors.tooltip}
          mainTooltip={dict.formatting_toolbar.colors.tooltip}
          icon={
            <ColorIcon
              textColor={state.textColor}
              backgroundColor={state.backgroundColor}
              size={20}
            />
          }
        />
      </Components.Generic.Menu.Trigger>
      <Components.Generic.Menu.Dropdown className="bn-menu-dropdown bn-color-picker-dropdown">
        <Components.Generic.Menu.Label>
          {dict.color_picker.text_title}
        </Components.Generic.Menu.Label>
        {renderItems('text', state.textColor)}
        <Components.Generic.Menu.Label>
          {dict.color_picker.background_title}
        </Components.Generic.Menu.Label>
        {renderItems('background', state.backgroundColor)}
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}

/** Toolbar entries for the image block's float prop, in display order. */
const IMAGE_FLOAT_OPTIONS = [
  { value: 'left', label: 'Float left' },
  { value: 'none', label: 'No float' },
  { value: 'right', label: 'Float right' },
] as const;

/**
 * Toolbar icon for a float option: a small image square with text lines
 * beside it (left/right) or below it (none). Inline SVG — react-icons is not
 * an admin-client dependency.
 */
function ImageFloatIcon({ side }: { side: ImageFloat }) {
  const squareX = side === 'left' ? 3 : side === 'right' ? 15 : 9;
  return (
    <svg
      fill="none"
      height="16"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      width="16"
    >
      <rect height="8" width="8" x={squareX} y="3" rx="1" />
      {side === 'none' ? (
        <path d="M5 15h14M5 18h14" strokeLinecap="round" />
      ) : (
        <path
          d={side === 'left' ? 'M14 5h7M14 8h7M5 15h16M5 18h16' : 'M3 5h7M3 8h7M3 15h16M3 18h16'}
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/**
 * Float toggle group for the image block, shown in the shared formatting
 * toolbar when the cursor's block is an image (same guard shape as the
 * built-in TextAlignButton, which appears for images through the image
 * block's textAlignment prop). Writes the float prop; persistence is the
 * generic propSchema attribute layer (data-float on the <img>).
 */
function ImageFloatButtons() {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    typeof mainContentSchema.blockSchema,
    typeof mainContentSchema.inlineContentSchema,
    typeof mainContentSchema.styleSchema
  >();

  const state = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor.isEditable) {
        return undefined;
      }
      const block = editor.getTextCursorPosition().block;
      if (block.type !== 'image') {
        return undefined;
      }
      return { float: block.props.float };
    },
  });

  if (state === undefined) {
    return null;
  }

  function setFloat(value: ImageFloat) {
    const block = editor.getTextCursorPosition().block;
    editor.updateBlock(block, { props: { float: value } });
    // timeout needed for compatibility with Mantine Toolbar useFocusTrap
    setTimeout(() => editor.focus());
  }

  return (
    <>
      {IMAGE_FLOAT_OPTIONS.map(({ value, label }) => (
        <Components.FormattingToolbar.Button
          key={value}
          className="bn-button"
          data-test={`imageFloat${value[0].toUpperCase() + value.slice(1)}`}
          label={label}
          mainTooltip={label}
          isSelected={state.float === value}
          onClick={() => setFloat(value)}
          icon={<ImageFloatIcon side={value} />}
        />
      ))}
    </>
  );
}

/**
 * Hidden-anchor predicate (task 050-08, QA #60): the toolbar's anchor (the
 * selection) has real layout. An anchor inside a collapsed Toggle List — or
 * any display:none subtree — measures 0×0 at the page origin, and floating-ui
 * then positions the toolbar at that origin: the page's top-left corner over
 * the sidebar (qa/reports/2026-09-09.md#60). Exported for the unit tests,
 * which drive the anchor rect through the Range.getBoundingClientRect shim.
 */
export function isAnchorLaidOut(rect: DOMRect): boolean {
  return rect.width > 0 || rect.height > 0;
}

/**
 * The default formatting toolbar with the built-in ColorStyleButton replaced
 * by ExtendedColorStyleButton, matched by the upstream element key
 * "colorStyleButton", plus the image float toggle group. Rendered via
 * FormattingToolbarController (the formattingToolbar={false} prop on
 * BlockNoteView disables the default).
 *
 * Hidden-anchor guard (task 050-08, QA #60): while the anchor has no layout —
 * the selection sits inside a collapsed Toggle List, whose children the block
 * CSS hides with display:none — the toolbar renders as nothing instead of
 * floating to the page's top-left corner; expanding the toggle re-shows it
 * beside the selection. The controller itself keeps the selection "shown", so
 * hiding is our component's job. The measurement re-runs on every editor
 * transaction (selection moves, doc edits) AND on clicks inside the editor
 * DOM, because a toggle's collapse/expand dispatches none: the .bn-toggle-
 * button handler only flips a data-show-children attribute (ProseMirror is
 * told to ignore exactly that mutation in createToggleWrapper) and the open
 * state goes to localStorage — so the click is the only signal that the
 * anchor's layout may have changed.
 */
function ExtendedFormattingToolbar() {
  const editor = useBlockNoteEditor<
    typeof mainContentSchema.blockSchema,
    typeof mainContentSchema.inlineContentSchema,
    typeof mainContentSchema.styleSchema
  >();
  // Editor-event trigger: this subscribes the component to the editor's
  // selection/doc events, re-rendering it on each while the toolbar is shown
  // (the hook's selected value is deliberately unused — the fresh read
  // happens in the render body below).
  useEditorState({
    editor,
    selector: (snapshot) => snapshot.transactionNumber,
  });
  // DOM-click trigger: covers the toggle collapse/expand above, which no
  // editor event follows. The state value is deliberately unused — only the
  // re-render matters.
  const [, bumpClickEpoch] = useState(0);
  useEffect(() => {
    // Bubble phase, so the .bn-toggle-button's own click handler has already
    // flipped data-show-children when this measures.
    const dom = editor.domElement;
    if (!dom) {
      return;
    }
    const onClick = () => bumpClickEpoch((epoch) => epoch + 1);
    dom.addEventListener('click', onClick);
    return () => dom.removeEventListener('click', onClick);
  }, [editor]);
  // The anchor rect is re-read on every render; renders happen when either
  // trigger above advances (or the controller re-renders for its own
  // reasons, which is equally safe).
  const rect = editor.getSelectionBoundingBox();
  if (rect === undefined || !isAnchorLaidOut(rect)) {
    return null;
  }
  return (
    <FormattingToolbar>
      {getFormattingToolbarItems().map((item) =>
        item.type === ColorStyleButton ? <ExtendedColorStyleButton key="colorStyleButton" /> : item
      )}
      <ImageFloatButtons />
    </FormattingToolbar>
  );
}

/**
 * MainContentEditor (task 042-02, BlockNote port) — controlled WYSIWYG wrapper
 * for the campaign form's "Main Content" field. The stored format is unchanged
 * (an HTML fragment in `html_content`); the authoring surface is BlockNote
 * (Mantine flavor: self-contained compiled CSS. The shadcn flavor needs a
 * Tailwind v4 host — its chrome uses host-generated Tailwind utilities, and
 * this project is on Tailwind 3).
 *
 * Controlled bridge, same contract as the TipTap implementation (task 042-02):
 * - value: replaced into the document when it changes from OUTSIDE the editor
 *   (campaign load, template apply). A round-trip of our own emission
 *   (value === lastEmitted) is recognized as internal and skipped. An external
 *   replace stays silent (HF-023): the replace's own onChange is a programmatic
 *   echo of the value the form already holds, so it is suppressed and
 *   lastEmitted records the external value — the stored fragment stays
 *   byte-identical to what was loaded until the user edits (bug #50's
 *   "already applied" no-op depends on it).
 * - onChange: emits the serialized fragment on every USER-driven doc update;
 *   an emptied doc emits '' (never "<p></p>" noise) — empty-field semantics
 *   preserved.
 * - disabled: read-only surface.
 */
export function MainContentEditor({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Main Content',
}: MainContentEditorProps) {
  const editor = useCreateBlockNote({
    schema: mainContentSchema,
    // Carries the multi_column chunk the xl package's menus read (see the
    // comment at mainContentDictionary above).
    dictionary: mainContentDictionary,
    // Image upload (experiment branch blocknote-upgrades): with uploadFile
    // set, the image block's insert menu grows an Upload tab and image
    // drag-and-drop uploads too. Files go through the same multipart
    // endpoint as the campaign form's Image field (POST /admin/campaign-assets,
    // epic 095: PNG/JPEG/WebP/GIF, 5MB cap) and the block stores the minted
    // absolute URL — the campaign keeps holding only the HTML fragment. A
    // failed upload rejects here and the image block stays without a URL.
    uploadFile: async (file) => {
      const { url } = await api.uploadCampaignImage(file);
      return url;
    },
    // Colour whitelist on the paste path (task 050-07): an external
    // text/html clipboard is parsed, colour-normalized, re-serialized lossy
    // and pasted as clean HTML. The clipboard MIME types are checked before
    // the text/html branch, mirroring the default handler's own priority, so
    // everything else keeps the default behaviour: BlockNote's internal
    // format (every copy from within the editor also carries `blocknote/html`
    // — it must keep its exact markup, and needs no cleaning once the
    // document holds only palette names), VSCode, markdown, plain text and
    // file pastes.
    pasteHandler: ({ event, defaultPasteHandler }) => {
      const types = event.clipboardData?.types;
      if (
        !types ||
        !types.includes('text/html') ||
        types.includes('blocknote/html') ||
        types.includes('vscode-editor-data') ||
        types.includes('text/markdown')
      ) {
        return defaultPasteHandler();
      }
      const html = event.clipboardData.getData('text/html');
      // The outer `editor` is the same instance the handler context carries
      // (the option type erases the context's schema generics to `any`, while
      // the hook's return type keeps them precise for the normalizer).
      editor.pasteHTML(
        editor.blocksToHTMLLossy(normalizeColours(editor.tryParseHTMLToBlocks(html)))
      );
      return true;
    },
  });
  const hostRef = useRef<HTMLDivElement | null>(null);
  // The HTML emitted by the last internal edit; null = the current value came
  // from outside.
  const lastEmitted = useRef<string | null>(null);
  // True while the hydration effect's programmatic replaceBlocks is running.
  // That replace fires BlockNoteView's onChange SYNCHRONOUSLY inside the call
  // (transact → ProseMirror dispatch → the TipTap "update" event → the
  // subscriber, none of it deferred), and the emission is the echo of an
  // outside value, not a user edit — the form already holds it, so it must not
  // round-trip back through onChange (HF-023).
  const replacingExternally = useRef(false);

  // External value changes replace the document; internal emissions round-trip.
  useEffect(() => {
    if (value === lastEmitted.current) {
      return;
    }
    lastEmitted.current = null;
    try {
      // Colour whitelist (task 050-07): normalize the parsed tree before the
      // replace, so a stored phantom rgb() value never re-enters the
      // document. The replace itself stays silent (HF-023, below).
      const blocks = normalizeColours(
        editor.tryParseHTMLToBlocks(value.split('<br/>').join('<p>￼</p>') || '<p></p>')
      );
      replacingExternally.current = true;
      editor.replaceBlocks(editor.document, blocks);
      replacingExternally.current = false;
      // The external value is now the document's content: record it so the
      // next render's effect recognizes it (no re-replace), while a later
      // internal edit still emits (its onChange runs unsuppressed and
      // overwrites lastEmitted with its own serialization).
      lastEmitted.current = value;
    } catch (error) {
      replacingExternally.current = false;
      // Malformed stored HTML must not take the form down — keep the current
      // document and surface the parse failure.
      console.warn('MainContentEditor: failed to parse value as HTML', error);
    }
  }, [editor, value]);

  // Restore the 042-02 a11y contract: a contenteditable div is not a labelable
  // element, so the form's <label> does not associate to it — name the
  // editable surface explicitly. BlockNoteView creates the .bn-editor div in
  // its own (child) effect, which runs before this parent effect.
  useEffect(() => {
    hostRef.current
      ?.querySelector<HTMLElement>('.bn-editor')
      ?.setAttribute('aria-label', ariaLabel);
  }, [editor, ariaLabel]);

  return (
    <div className="main-content-editor" ref={hostRef}>
      <BlockNoteView
        editor={editor}
        editable={!disabled}
        // Pin the light theme: BlockNote otherwise follows the OS
        // prefers-color-scheme, so on a dark-mode OS the editor flipped to its
        // dark surface (gray #cfcfcf text on near-black #1f1f1f) while the
        // rest of the admin app — which is light-only — stayed light. Authored
        // content is styled for the white landing surfaces anyway (HF-021).
        theme="light"
        formattingToolbar={false}
        // Same pattern as the formatting toolbar above: the built-in slash
        // menu is disabled and this controller re-creates it with the
        // multi-column items ("Columns 2" / "Columns 3") appended to the
        // defaults. Rendering the controller replaces the default "/" menu,
        // which knows nothing about the xl package's items.
        slashMenu={false}
        onChange={(e) => {
          if (replacingExternally.current) {
            // The hydration effect's own replaceBlocks echoing back — not a
            // user edit; the effect records the external value itself.
            return;
          }
          lastEmitted.current = stripObjectReplacementParagraphs(
            selfContainedColumnFlex(e.blocksToHTMLLossy(e.document))
          );
          onChange(lastEmitted.current);
        }}
      >
        <FormattingToolbarController formattingToolbar={ExtendedFormattingToolbar} />
        <SuggestionMenuController
          triggerCharacter="/"
          getItems={async (query) =>
            filterSuggestionItems(
              [...getDefaultReactSlashMenuItems(editor), ...getMultiColumnSlashMenuItems(editor)],
              query
            )
          }
        />
      </BlockNoteView>
    </div>
  );
}
